import React, { useEffect, useRef, useState } from 'react';
import { TextField, Select, MenuItem, Button, InputLabel, FormControl, SelectChangeEvent } from '@mui/material';
import { Departement, GeneriqueData, Reglement, Tiers } from '../../models/models';
import { TypeReglement, TypeTiers } from '../../constants/enums';
import { labels } from '../../constants/labels';
import SelectModeReglement from '../Selects/SelectModeReglement';
import SelectNatureReglement from '../Selects/SelectNatureReglement';
import SelectCaisse from '../Selects/SelectCaisse';
import SelectDepartement from '../Selects/SelectDepartement';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from '../../components/SnackbarProvider';
import { createReglement, fetchReglementData, setSelectedReglement } from '../../reduxStore/ReglementSlice';
import { AppDispatch, RootState } from '../../reduxStore/Store';
import ClientAutoC from '../AutoComplete/ClientAutoC';
import FournisseurAutoC from '../AutoComplete/FournisseurAutoC';
import EmployeAutoC from '../AutoComplete/EmployeAutoC';

interface ReglementFormProps {
  /** Fermeture du panneau parent (drawer) — appelée sur Annuler (édition). */
  onClose?: () => void;
}

const ReglementForm: React.FC<ReglementFormProps> = ({ onClose }) => {
  const defaultTypeReglement = TypeReglement.ENCAISSEMENT;
  const montantRef = useRef<HTMLInputElement>(null);
  // Après un enregistrement, le formulaire reste ouvert en saisie enchaînée : les
  // champs conservés (caisse, mode, tiers…) ne doivent plus être écrasés par le
  // pré-remplissage depuis les filtres.
  const keepEntryRef = useRef(false);

  const { selectedReglement, filters } = useSelector((state: any) => state.ReglementSlice);
  const { selectedDepartement } = useSelector((state: RootState) => state.AppSlice);
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();

  const [formData, setFormData] = useState<Reglement>({
    id: -1,
    type_reglement: defaultTypeReglement,
    date_reglement: new Date().toISOString().split('T')[0],
    numero_reglement: '',
    montant: 0,
    sens: defaultTypeReglement === TypeReglement.ENCAISSEMENT ? 'C' : 'D',
    reference: '',
    mode_paiement_id: null,
    nature_id: null,
    tiers_id: null,
    tiers: null,
    nature: null,
    mode_paiement: null,
    caisse: null,
    departement_id: selectedDepartement?.id ?? null,
    departement: selectedDepartement ?? null,
  });

  // "mode" UI : distingue Paiement fournisseur vs Paiement employé — tous deux
  // sont des type_reglement=PAIEMENT (sens D). Le mode pilote seulement quel
  // sélecteur de tiers afficher.
  const PAIEMENT_EMPLOYE = 'PAIEMENT_EMPLOYE';
  const [mode, setMode] = useState<string>(defaultTypeReglement);

  useEffect(() => {
    if (selectedReglement) {
      setMode(
        selectedReglement.type_reglement === TypeReglement.PAIEMENT &&
        selectedReglement.tiers?.type === TypeTiers.EMPLOYE
          ? PAIEMENT_EMPLOYE
          : selectedReglement.type_reglement
      );
      setFormData(prev => ({
        ...prev,
        id: selectedReglement.id,
        numero_reglement: selectedReglement.numero_reglement,
        type_reglement: selectedReglement.type_reglement,
        date_reglement: new Date(selectedReglement.date_reglement).toISOString().split('T')[0],
        montant: selectedReglement.montant,
        sens: selectedReglement.sens,
        reference: selectedReglement.reference,
        mode_paiement_id: selectedReglement.mode_paiement_id,
        nature_id: selectedReglement.nature_id,
        tiers_id: selectedReglement.tiers_id,
        tiers: selectedReglement.tiers,
        caisse_id: selectedReglement.caisse_id,
        nature: selectedReglement.nature,
        caisse: selectedReglement.caisse,
        mode_paiement: selectedReglement.mode_paiement,
        bien_id: selectedReglement.bien_id,
        bien: selectedReglement.bien,
        departement_id: selectedReglement.departement_id,
        departement: selectedReglement.departement,
      }));
    } else if (filters && !keepEntryRef.current) {
      // Pré-remplissage de confort à la création — valeurs par défaut depuis le filtre.
      // Le filtre « Paiement employé » = PAIEMENT + tiers_type EMPLOYE → même mode UI.
      const typeFiltre = filters.type_reglement || defaultTypeReglement;
      setMode(
        typeFiltre === TypeReglement.PAIEMENT && filters.tiers_type === TypeTiers.EMPLOYE
          ? PAIEMENT_EMPLOYE
          : typeFiltre
      );
      setFormData(prev => ({
        ...prev,
        type_reglement: typeFiltre,
        sens: filters.sens || (filters.type_reglement === TypeReglement.ENCAISSEMENT ? 'C' : 'D'),
        tiers_id: filters.tiers_id,
        tiers: filters.tiers,
        caisse_id: filters.caisse_id,
        caisse: filters.caisse,
        // ?? prev.departement_id : préserve un choix déjà fait via le sélecteur en
        // mode Tous — sinon un re-render déclenché par un changement de filtre
        // (pagination, etc.) l'effacerait silencieusement.
        departement_id: selectedDepartement?.id ?? prev.departement_id,
        departement: selectedDepartement ?? prev.departement,
      }));
    }
  }, [selectedReglement, filters, defaultTypeReglement, selectedDepartement]);

  useEffect(() => {
    if (!selectedReglement) {
      setFormData(prev => ({
        ...prev,
        sens: prev.type_reglement === TypeReglement.ENCAISSEMENT ? 'C' : 'D',
      }));
    }
  }, [formData.type_reglement, selectedReglement]);

  const handleInputChange = (field: keyof Reglement) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSelectChange = (field: keyof Reglement) => (event: SelectChangeEvent) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleModeChange = (event: SelectChangeEvent) => {
    const m = event.target.value;
    const type = (m === PAIEMENT_EMPLOYE ? TypeReglement.PAIEMENT : m) as Reglement['type_reglement'];
    setMode(m);
    setFormData(prev => ({
      ...prev,
      type_reglement: type,
      sens: type === TypeReglement.PAIEMENT ? 'D' : type === TypeReglement.ENCAISSEMENT ? 'C' : prev.sens,
      // le tiers change de nature (client / fournisseur / employé) → on le réinitialise
      tiers_id: null,
      tiers: null,
      // la nature ne s'applique qu'au mouvement et au paiement employé → reset
      nature_id: null,
      nature: null,
    }));
  };

  // Mode "Tous départements" (création uniquement — l'édition préserve toujours le
  // departement_id existant du règlement) : le département n'a plus de valeur implicite,
  // l'utilisateur doit le choisir avant de pouvoir sélectionner une caisse.
  const handleDepartementChange = (dept: Departement | null) => {
    setFormData(prev => ({
      ...prev,
      departement_id: dept?.id ?? null,
      departement: dept,
      caisse_id: null,
      caisse: null,
    }));
  };

  const handleCaisseChange = (caisse: GeneriqueData | null) => {
    setFormData(prev => ({
      ...prev,
      caisse_id: caisse ? caisse.id : null,
      caisse: caisse ? caisse : null,
    }));
  };

  const handleTiersChange = (tiers: Tiers | null) => {
    setFormData(prev => ({
      ...prev,
      tiers_id: tiers ? tiers.id : null,
      tiers: tiers ? tiers : null,
    }));
  };

  const handleOnModeReglementChange = (modePaiement: GeneriqueData | null) => {
    setFormData(prev => ({
      ...prev,
      mode_paiement_id: modePaiement ? modePaiement.id : 0,
      mode_paiement: modePaiement,
    }));
  };

  const handleNatureReglementChange = (natureReglement: GeneriqueData | null) => {
    setFormData(prev => ({
      ...prev,
      nature_id: natureReglement ? natureReglement.id : null,
      nature: natureReglement,
    }));
  };

  const validateForm = (): { isValid: boolean; messages: string[] } => {
    const messages: string[] = [];

    if (!formData.departement_id) {
      messages.push('Le département est obligatoire.');
    }
    if (!formData.caisse_id) {
      messages.push('La caisse est obligatoire.');
    }
    if (!formData.date_reglement) {
      messages.push('La date de règlement est obligatoire.');
    }
    if (!formData.montant) {
      messages.push('Le montant est obligatoire.');
    }
    if (formData.type_reglement !== TypeReglement.MOUVEMENT && !formData.tiers_id) {
      const cible = mode === TypeReglement.ENCAISSEMENT ? labels.client
        : mode === PAIEMENT_EMPLOYE ? labels.employe
        : labels.fournisseur;
      messages.push(`Le ${cible} est obligatoire.`);
    }
    if (formData.type_reglement === TypeReglement.MOUVEMENT && !formData.sens) {
      messages.push('Le sens est requis pour un mouvement.');
    }
    // Paiement employé = charge dans le compte de résultat → nature obligatoire
    // (catégorise l'écriture ; sans elle, elle serait NEUTRE et hors résultat).
    if (mode === PAIEMENT_EMPLOYE && !formData.nature_id) {
      messages.push('La nature de règlement est obligatoire pour un paiement employé.');
    }
    if (!formData.mode_paiement_id) {
      messages.push('Le mode de paiement est obligatoire.');
    }

    return { isValid: messages.length === 0, messages };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { isValid, messages } = validateForm();

    if (!isValid) {
      messages.forEach((msg) => showSnackbar(msg, 'error'));
      return;
    }
    try {
      await dispatch(createReglement(formData)).unwrap();
      prepareNextEntry();
      await dispatch(fetchReglementData(filters));
      showSnackbar('Opération enregistrée avec succès !', 'success');
    } catch (error) {
      showSnackbar((error as string) || 'Erreur lors de l\'enregistrement', 'error');
    }
  };

  // Saisie enchaînée : le drawer reste ouvert, on repasse en création en conservant
  // type, date, département, caisse, mode de règlement et tiers ; seuls les champs
  // propres à l'opération sont vidés. La réinitialisation complète a lieu à la
  // fermeture du drawer (démontage du formulaire).
  const prepareNextEntry = () => {
    keepEntryRef.current = true;
    setFormData(prev => ({
      ...prev,
      id: -1,
      numero_reglement: '',
      montant: 0,
      reference: '',
      nature_id: null,
      nature: null,
      bien_id: null,
      bien: null,
    }));
    dispatch(setSelectedReglement(null));
    setTimeout(() => montantRef.current?.focus(), 0);
  };

  const isCreating = formData.id === -1;

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
      <FormControl fullWidth size="small">
        <InputLabel id="type_reglement_label">Type de règlement</InputLabel>
        <Select
          labelId="type_reglement_label"
          value={mode || ''}
          onChange={handleModeChange}
          label="Type de règlement"
          size="small"
        >
          <MenuItem value={TypeReglement.ENCAISSEMENT}>{labels.reglement} {labels.client}</MenuItem>
          <MenuItem value={TypeReglement.PAIEMENT}>{labels.reglement} {labels.fournisseur}</MenuItem>
          <MenuItem value={PAIEMENT_EMPLOYE}>{labels.reglement} {labels.employe}</MenuItem>
          <MenuItem value={TypeReglement.MOUVEMENT}>Mouvement De Caisse</MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Date de règlement"
        type="date"
        value={formData.date_reglement || ''}
        onChange={handleInputChange('date_reglement')}
        InputLabelProps={{ shrink: true }}
        fullWidth
        required
        size='small'
      />

      {!isCreating && (
        <TextField
          label="Numéro de règlement"
          value={formData.numero_reglement || ''}
          InputProps={{ readOnly: true }}
          fullWidth
          size='small'
        />
      )}

      {isCreating && !selectedDepartement && (
        <SelectDepartement
          onDepartementChange={handleDepartementChange}
          selectedDepartement={formData.departement}
        />
      )}

      <SelectCaisse
        onCaisseChange={handleCaisseChange}
        selectedCaisse={formData.caisse}
        departement_id={formData.departement_id}
      />

      {mode === TypeReglement.ENCAISSEMENT && (
        <ClientAutoC
          onClientChange={handleTiersChange}
          selectedClient={formData.tiers}
          required
        />
      )}

      {mode === TypeReglement.PAIEMENT && (
        <FournisseurAutoC
          onFournisseurChange={handleTiersChange}
          selectedFournisseur={formData.tiers}
          required
        />
      )}

      {mode === PAIEMENT_EMPLOYE && (
        <EmployeAutoC
          onEmployeChange={handleTiersChange}
          selectedEmploye={formData.tiers}
          required
        />
      )}

      <TextField
        label="Montant"
        type="number"
        value={formData.montant || ''}
        onChange={handleInputChange('montant')}
        inputRef={montantRef}
        fullWidth
        size='small'
      />

      {formData.type_reglement === TypeReglement.MOUVEMENT && (
        <FormControl fullWidth>
          <InputLabel id="sens_label">Sens</InputLabel>
          <Select
            labelId="sens_label"
            value={formData.sens || ''}
            onChange={handleSelectChange('sens')}
            required
            size="small"
          >
            <MenuItem value="D">Débit</MenuItem>
            <MenuItem value="C">Crédit</MenuItem>
          </Select>
        </FormControl>
      )}

      <TextField
        label="Référence"
        value={formData.reference || ''}
        onChange={handleInputChange('reference')}
        fullWidth
        size='small'
      />

      <SelectModeReglement
        onModeReglementChange={handleOnModeReglementChange}
        selectedModeReglement={formData.mode_paiement}
      />

      {(formData.type_reglement === TypeReglement.MOUVEMENT || mode === PAIEMENT_EMPLOYE) && (
        <SelectNatureReglement
          onNatureReglementChange={handleNatureReglementChange}
          selectedNatureReglement={formData.nature}
        />
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="contained" color="primary" type="submit">
          {isCreating ? 'Créer' : 'Modifier'} Règlement
        </Button>
        {!isCreating && (
          <Button variant="outlined" color="inherit" type="button" onClick={() => onClose?.()}>
            Annuler
          </Button>
        )}
      </div>
    </form>
  );
};

export default ReglementForm;
